import { T } from "@repo/transifex";
import { Button } from "~/components";
import type { ScaleUnit } from "~/types/hardware";
import { formatWeightReading, getScaleUnitLabel } from "~/utils/weight-units";

import { DecimalsSlider } from "./decimals-slider";
import { ScaleStatusBadge, ScaleStatusView } from "./scale-status-view";
import type { ScaleTrialPhase } from "./use-scale-trial";

export type ScaleReading = {
  weight: number;
  unit: ScaleUnit;
  decimals: number;
};

type ScalePrecisionStepProps = {
  phase: ScaleTrialPhase | "confirmed";
  modelLabel: string;
  reading: ScaleReading | null;
  decimals: number;
  /** False while the slider has moved but the scale has not answered with that precision yet. */
  canConfirm?: boolean;
  onDecimalsChange: (decimals: number) => void;
  onConfirm: () => void;
  onBack: () => void;
  onRetry: () => void;
  onDone: () => void;
};

/** Step 2 of pairing: weigh something and dial the decimals until the number matches the scale's display. */
export const ScalePrecisionStep = ({
  phase,
  modelLabel,
  reading,
  decimals,
  canConfirm = true,
  onDecimalsChange,
  onConfirm,
  onBack,
  onRetry,
  onDone
}: ScalePrecisionStepProps) => (
  <div className="flex w-full flex-col gap-6">
    <div className="flex items-center justify-between">
      <p className="text-xs font-bold uppercase tracking-wide text-text-secondary">
        <T _str="Step 2 of 2" />
      </p>

      {phase === "stable" ? (
        <ScaleStatusBadge tone="live">
          <T _str="Stable" />
        </ScaleStatusBadge>
      ) : null}
      {phase === "connecting" || phase === "waiting" ? (
        <ScaleStatusBadge tone="live">
          <T _str="Live from {model}" model={modelLabel} />
        </ScaleStatusBadge>
      ) : null}
      {phase === "error" ? (
        <ScaleStatusBadge tone="error">
          <T _str="No reading" />
        </ScaleStatusBadge>
      ) : null}
    </div>

    {phase === "connecting" || phase === "waiting" ? (
      <ScaleStatusView
        actions={
          <Button
            className="min-w-[200px]"
            onClick={onBack}
            variant="secondary"
          >
            <T _str="Cancel" />
          </Button>
        }
        description={
          <T _str="Waiting for a stable reading. Keep hands off the platter and do not move the item while it settles." />
        }
        title={<T _str="Place an item on the scale" />}
        tone="loading"
      />
    ) : null}

    {phase === "stable" && reading ? (
      <div className="flex w-full flex-col items-center gap-6 text-center">
        <p className="text-xs font-bold uppercase tracking-wide text-text-secondary">
          <T _str="This is what customers will see" />
        </p>

        <p className="text-6xl font-bold text-primary-main">
          {formatWeightReading(reading)}
        </p>

        <p className="text-base text-text-secondary">
          <T _str="Look at the scale's own display. Slide until this number matches it." />
        </p>

        <DecimalsSlider onChange={onDecimalsChange} value={decimals} />

        <div className="flex w-full gap-4">
          <Button className="flex-1" onClick={onBack} variant="secondary">
            <T _str="Back" />
          </Button>
          <Button
            className="flex-[1.4]"
            disabled={!canConfirm}
            onClick={onConfirm}
            variant="primary"
          >
            <T _str="Confirm precision" />
          </Button>
        </div>
      </div>
    ) : null}

    {phase === "confirmed" ? (
      <ScaleStatusView
        actions={
          <Button className="min-w-[200px]" onClick={onDone} variant="primary">
            <T _str="Back to settings" />
          </Button>
        }
        description={
          reading ? (
            <T
              _str="Weights will show with {count} decimals in {unit}, matching what you saw on the scale."
              count={reading.decimals}
              unit={getScaleUnitLabel(reading.unit)}
            />
          ) : (
            <T _str="Weights will show the way you saw them on the scale." />
          )
        }
        title={<T _str="Precision confirmed" />}
        tone="success"
      />
    ) : null}

    {phase === "error" ? (
      <ScaleStatusView
        actions={
          <>
            <Button className="flex-1" onClick={onDone} variant="secondary">
              <T _str="Back to settings" />
            </Button>
            <Button className="flex-[1.4]" onClick={onRetry} variant="primary">
              <T _str="Retry" />
            </Button>
          </>
        }
        description={
          <T _str="Check that the scale is powered on and connected, and that the platter is clear, then try again." />
        }
        title={<T _str="Couldn't get a stable reading" />}
        tone="error"
      />
    ) : null}
  </div>
);
